import { useState } from "react";
import type { MouseEvent } from "react";
import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import CheckIcon from "@mui/icons-material/Check";

import useTalentApi from "components/Screens/Marketplace/Customer/TalentApiContext";
import { useShortlist } from "components/Screens/Marketplace/Customer/Shortlist/ShortlistContext";
import ShortlistFullPopup from "components/Screens/Marketplace/Customer/Shortlist/ShortlistFullPopup";
import "./Shortlist.scss";

interface Props {
  talentId: string;
  interviewRequested?: boolean;
  size?: "card" | "profile";
  fullWidth?: boolean;
}

function SaveToShortlistButton({ talentId, interviewRequested = false, size = "card", fullWidth = false }: Props) {
  const { hasPermission, routes } = useTalentApi();
  const { isShortlisted, isFull, add, remove } = useShortlist();
  // The state the click asked for, shown at once while the call runs; cleared after, so the button shows the
  // shortlist as the server has it (back to the old state when the call failed).
  const [pending, setPending] = useState<boolean | null>(null);
  const [fullPopup, setFullPopup] = useState(false);
  if (!hasPermission("talentNetwork.shortlist")) return null;

  const cls = `sl-save sl-save--${size}${fullWidth ? " sl-save--block" : ""}`;

  if (interviewRequested) {
    return (
      <button type="button" className={`${cls} sl-save--requested`} disabled aria-label="Interview requested">
        <CheckIcon fontSize="small" />
        Interview requested
      </button>
    );
  }

  const saved = pending ?? isShortlisted(talentId);
  // Only with a cap (none today): a save past it points the customer at Request a hire / a meeting instead.
  const blocked = !saved && isFull;
  const onClick = async (e: MouseEvent) => {
    e.stopPropagation(); // cards are clickable; saving must not open the profile
    if (pending !== null) return;
    if (blocked) {
      setFullPopup(true);
      return;
    }
    setPending(!saved);
    try {
      if (saved) await remove(talentId);
      else await add(talentId, size);
    } catch {
      // the shortlist keeps its last known state
    } finally {
      setPending(null);
    }
  };

  return (
    <>
      <button type="button" className={`${cls}${saved ? " sl-save--saved" : ""}`} onClick={onClick} aria-busy={pending !== null}>
        {saved ? <BookmarkIcon fontSize="small" /> : <BookmarkBorderOutlinedIcon fontSize="small" />}
        {saved ? "Shortlisted" : "Shortlist"}
      </button>
      {fullPopup && (
        // The dialog renders in a portal; stop clicks bubbling to a clickable card behind it.
        <span onClick={(e) => e.stopPropagation()} role="presentation">
          <ShortlistFullPopup requestAHireRoute={routes.requestAHire} onClose={() => setFullPopup(false)} />
        </span>
      )}
    </>
  );
}

export default SaveToShortlistButton;
